/**
 * =============================================================================
 * IDS CRM Web - Microsoft SSO flow helpers (Cognito, code + PKCE)
 * =============================================================================
 *
 * The login page fetches GET /auth/sso-config at runtime (nothing is baked
 * into the bundle), sends the browser to the Cognito hosted authorize
 * endpoint with a PKCE challenge, and /auth/callback exchanges the returned
 * code for tokens at Cognito before swapping the ID token for the CRM's own
 * JWT at POST /auth/sso.
 *
 * =============================================================================
 */

const VERIFIER_KEY = 'ids-crm-sso-verifier';
const STATE_KEY = 'ids-crm-sso-state';

const base64UrlEncode = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomString = (byteLength) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

export const ssoRedirectUri = () => `${window.location.origin}/auth/callback`;

/** Stores PKCE verifier + state and sends the browser to Microsoft. */
export async function beginMicrosoftLogin(config) {
  const verifier = randomString(48);
  const state = randomString(24);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  const challenge = base64UrlEncode(digest);

  const params = new URLSearchParams({
    client_id: config.client_id,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: ssoRedirectUri(),
    identity_provider: config.identity_provider || 'Microsoft',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256'
  });

  window.location.assign(`${config.domain}/oauth2/authorize?${params.toString()}`);
}

/**
 * Validates state, exchanges the code at Cognito's token endpoint (PKCE — no
 * client secret in the SPA) and returns the ID token.
 */
export async function completeMicrosoftLogin(config, code, returnedState) {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Sign-in state mismatch — please try again.');
  }
  if (!verifier) {
    throw new Error('Sign-in session expired — please try again.');
  }

  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.client_id,
      code,
      redirect_uri: ssoRedirectUri(),
      code_verifier: verifier
    }).toString()
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.id_token) {
    throw new Error(data.error_description || data.error || 'Microsoft sign-in failed — please try again.');
  }
  return data.id_token;
}
